import { normalizeInstagramHandle } from "@lib/utils";
import { useAction } from "convex/react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../../convex/_generated/api";
import { lookupProfile } from "../api/profile";
import { Footer } from "../components/landing/Footer";
import { Header } from "../components/landing/Header";
import { useAuth } from "../hooks/useAuth";
import { searchCache } from "../hooks/useProfileSearch";
import { t } from "../i18n";
import { savePendingPurchaseContext } from "../lib/pendingPurchase";
import { withTimeout } from "../lib/withTimeout";
import {
  getBrowserBillingCurrency,
  getLocalPriceFallback,
  WEB_PLAN_PRICES,
} from "../lib/localizedPricing";
import { proxiedImageUrl } from "../utils/imageProxy";

const FALLBACK_AVATARS = [
  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=face",
  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
  "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face",
  "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face",
  "https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=150&h=150&fit=crop&crop=face",
  "https://images.unsplash.com/photo-1517841905240-472988babdf9?w=150&h=150&fit=crop&crop=face",
];

export function PaywallPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const handle = searchParams.get("handle") ?? "";

  const { isAuthenticated, createAnonymousSession } = useAuth();
  const createPurchaseLink = useAction(api.stripe.createWebCheckoutLink);
  const billingCurrency = getBrowserBillingCurrency();

  const [selectedPlan, setSelectedPlan] = useState<"weekly" | "monthly">(
    "monthly",
  );
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileData, setProfileData] = useState<any>(null);
  const [previewAvatars, setPreviewAvatars] = useState<string[]>([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState(false);

  const getInitials = () => {
    if (profileData?.fullName) {
      const parts = profileData.fullName.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return parts[0].substring(0, 2).toUpperCase();
    }
    if (handle) {
      return handle
        .replace(/[^a-zA-Z0-9]/g, "")
        .substring(0, 2)
        .toUpperCase();
    }
    return "ST";
  };

  useEffect(() => {
    if (!handle) {
      setLoadingProfile(false);
      return;
    }

    setLoadingProfile(true);
    setErrorMsg(null);
    setAvatarError(false);

    lookupProfile(handle)
      .then((res) => {
        if (res.availability === "public_available" || res.profile) {
          setProfileData(res.profile);
          const normalized = normalizeInstagramHandle(handle);
          searchCache[normalized] = res;

          const apiAvatars = (res.previewFollowing || [])
            .map((f: any) => f.profilePicUrl)
            .filter((url: any) => !!url);

          const combined = [...apiAvatars];
          let fallbackIdx = 0;
          while (combined.length < 7 && fallbackIdx < FALLBACK_AVATARS.length) {
            combined.push(FALLBACK_AVATARS[fallbackIdx]);
            fallbackIdx++;
          }
          setPreviewAvatars(combined.slice(0, 7));
        } else {
          setProfileData({ handle });
          setPreviewAvatars(FALLBACK_AVATARS);
        }
      })
      .catch((err) => {
        console.error("Failed to load profile for paywall", err);
        setProfileData({ handle });
        setPreviewAvatars(FALLBACK_AVATARS);
      })
      .finally(() => {
        setLoadingProfile(false);
      });
  }, [handle]);

  const handleStartMonitoring = async () => {
    if (!handle) return;
    setErrorMsg(null);
    setActionLoading(true);

    try {
      await withTimeout(
        (async () => {
          if (!isAuthenticated) {
            await createAnonymousSession();
          }

          savePendingPurchaseContext({
            source: "public_paywall",
            handle,
          });

          const result = await createPurchaseLink({
            priceLookupKey: selectedPlan,
            currency: billingCurrency,
            handle,
            previewSessionId: undefined,
            returnTo: "paywall_success",
          });

          window.location.assign(result.url);
        })(),
      );
    } catch (err: any) {
      console.error("Failed to create checkout session", err);
      setErrorMsg(t("paywallErrorStartMonitoring"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleClose = () => {
    if (handle) {
      navigate(`/profile/${encodeURIComponent(handle)}`);
    } else {
      navigate("/");
    }
  };

  const planPrices = WEB_PLAN_PRICES[billingCurrency];
  const weeklyPrice = getLocalPriceFallback(planPrices.weekly, billingCurrency);
  const monthlyPrice = getLocalPriceFallback(planPrices.monthly, billingCurrency);
  const weeklyPeriod = t("paywallBillingPeriodWeekly");
  const monthlyPeriod = t("paywallBillingPeriodMonthly");
  const perDayPrice = getLocalPriceFallback(
    planPrices.monthly / 30,
    billingCurrency,
  );
  return (
    <div className="st-page st-paywall-page-wrapper">
      <Header />

      <main className="st-paywall-main">
        <div className="st-container">
          <div className="st-paywall-container">
            <button
              className="st-paywall-close-btn"
              onClick={handleClose}
              aria-label={t("commonClose")}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            {loadingProfile ? (
              <div
                className="st-paywall-skeleton"
                aria-busy="true"
                aria-label="Loading profile details"
              >
                <div className="st-paywall-skeleton-avatar shimmer" />
                <div className="st-paywall-skeleton-title shimmer" />
                <div className="st-paywall-skeleton-subtitle shimmer" />
                <div className="st-paywall-skeleton-row">
                  {[...Array(7)].map((_, i) => (
                    <div
                      key={i}
                      className="st-paywall-skeleton-circle shimmer"
                    />
                  ))}
                </div>
                <div className="st-paywall-skeleton-benefits shimmer" />
                <div className="st-paywall-skeleton-card shimmer" />
                <div className="st-paywall-skeleton-card shimmer" />
              </div>
            ) : (
              <div className="st-paywall-content">
                <div className="st-paywall-left-col">
                  <div className="st-paywall-header-section">
                    <div className="st-paywall-target-avatar-container">
                      {profileData?.profilePicUrl && !avatarError ? (
                        <img
                          src={proxiedImageUrl(profileData.profilePicUrl)}
                          alt={profileData.fullName || handle}
                          className="st-paywall-target-avatar"
                          referrerPolicy="no-referrer"
                          onError={() => setAvatarError(true)}
                        />
                      ) : (
                        <div className="st-paywall-target-avatar st-paywall-avatar-placeholder-fallback">
                          {getInitials()}
                        </div>
                      )}
                      <div className="st-paywall-avatar-pulse-ring" />
                    </div>

                    <h1 className="st-paywall-title">
                      {t("paywallTitleTarget", { handle })}
                    </h1>

                    <p className="st-paywall-subtitle">
                      {t("paywallSubtitleTarget", { handle })}
                    </p>
                  </div>

                  <div className="st-paywall-teaser-section">
                    <p className="st-paywall-teaser-title">
                      {t("paywallProfilesFollowing", { handle })}
                    </p>
                    <div className="st-paywall-avatar-row">
                      <div className="st-paywall-avatar-row-inner">
                        {previewAvatars.map((url, i) => (
                          <div key={i} className="st-paywall-avatar-item">
                            <img
                              src={proxiedImageUrl(url)}
                              alt=""
                              className="st-paywall-avatar-img"
                              referrerPolicy="no-referrer"
                            />
                            <div className="st-paywall-avatar-blur-overlay">
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="white"
                                strokeWidth="2.5"
                              >
                                <rect
                                  x="3"
                                  y="11"
                                  width="18"
                                  height="11"
                                  rx="2"
                                  ry="2"
                                ></rect>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                              </svg>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="st-paywall-benefits-section">
                    <div className="st-paywall-social-proof">
                      <div
                        className="st-paywall-stars"
                        aria-label="5 star rating"
                      >
                        {[...Array(5)].map((_, i) => (
                          <span key={i} className="st-paywall-star">
                            ★
                          </span>
                        ))}
                      </div>
                      <span className="st-paywall-trusted-text">
                        {t("paywallTrusted")}
                      </span>
                    </div>

                    <ul className="st-paywall-features-list">
                      <li>
                        <div className="st-paywall-feature-icon">✓</div>
                        <div>
                          <strong>{t("paywallFeaturePrivateTitle")}</strong>
                          <span>{t("paywallFeaturePrivateDesc")}</span>
                        </div>
                      </li>
                      <li>
                        <div className="st-paywall-feature-icon">✓</div>
                        <div>
                          <strong>{t("paywallFeatureRecentTitle")}</strong>
                          <span>{t("paywallFeatureRecentDesc")}</span>
                        </div>
                      </li>
                      <li>
                        <div className="st-paywall-feature-icon">✓</div>
                        <div>
                          <strong>{t("paywallFeatureNotifiedTitle")}</strong>
                          <span>{t("paywallFeatureNotifiedDesc")}</span>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="st-paywall-right-col">
                  <div className="st-paywall-plans-section">
                    {/* Weekly Plan */}
                    <div
                      className={`st-paywall-plan-card ${selectedPlan === "weekly" ? "active" : ""}`}
                      onClick={() => setSelectedPlan("weekly")}
                    >
                      <input
                        type="radio"
                        id="plan-weekly"
                        name="paywall-plan"
                        checked={selectedPlan === "weekly"}
                        onChange={() => setSelectedPlan("weekly")}
                        className="st-paywall-radio-input"
                      />
                      <div className="st-paywall-plan-details">
                        <div className="st-paywall-plan-name">
                          {t("paywallPlanWeekly")}
                        </div>
                        <div className="st-paywall-plan-desc">
                          {t("paywallPlanWeeklyDesc")}
                        </div>
                      </div>
                      <div className="st-paywall-plan-price-wrapper">
                        <span className="st-paywall-plan-price">
                          {weeklyPrice}
                        </span>
                        <span className="st-paywall-plan-period">
                          /{weeklyPeriod}
                        </span>
                      </div>
                    </div>

                    {/* Monthly Plan */}
                    <div
                      className={`st-paywall-plan-card ${selectedPlan === "monthly" ? "active" : ""}`}
                      onClick={() => setSelectedPlan("monthly")}
                    >
                      <div className="st-paywall-badge-best-value">
                        {t("paywallBestValue")}
                      </div>
                      <input
                        type="radio"
                        id="plan-monthly"
                        name="paywall-plan"
                        checked={selectedPlan === "monthly"}
                        onChange={() => setSelectedPlan("monthly")}
                        className="st-paywall-radio-input"
                      />
                      <div className="st-paywall-plan-details">
                        <div className="st-paywall-plan-name">
                          {t("paywallPlanMonthly")}
                        </div>
                        <div className="st-paywall-plan-desc">
                          {t("paywallPlanMonthlyDesc")}
                        </div>
                      </div>
                      <div className="st-paywall-plan-price-wrapper">
                        <span className="st-paywall-plan-price">
                          {monthlyPrice}
                        </span>
                        <span className="st-paywall-plan-period">
                          /{monthlyPeriod}
                        </span>
                      </div>
                    </div>
                  </div>

                  <p className="st-paywall-pricing-note">
                    {t("paywallPerDay", { price: perDayPrice })} •{" "}
                    {t("paywallCancelAnytime")}
                  </p>

                  {errorMsg && (
                    <div className="st-paywall-error" role="alert">
                      {errorMsg}
                    </div>
                  )}

                  <button
                    onClick={handleStartMonitoring}
                    disabled={actionLoading}
                    className="st-paywall-btn-pulse st-paywall-cta-button"
                  >
                    {actionLoading
                      ? t("paywallActivatingPlan")
                      : t("paywallCta")}
                  </button>

                  <div className="st-paywall-footer-actions">
                    <button
                      onClick={handleClose}
                      className="st-paywall-not-now-btn"
                    >
                      {t("paywallNotNow")}
                    </button>
                  </div>

                  <p className="st-paywall-terms-note">{t("paywallTerms")}</p>
                  <p className="st-paywall-billing-note">
                    {t("paywallBillingNote")}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
